import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptor,
  HttpStatusCode
} from '@angular/common/http';

import { merge, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, retry, switchMap, takeUntil } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { NotificationRef, NotificationService } from "@progress/kendo-angular-notification";
import { errorMessages } from '../constants/error.constant';
import { NavigationService } from '../services/framework';

@Injectable()

export class ErrorIntercept implements HttpInterceptor {

  destroy$ = new Subject();

  private notifications: NotificationRef[] = [];

  constructor(private notificationService: NotificationService
      , private navigationService: NavigationService) {
    this.subscribeToNavigation();
  }

  private subscribeToNavigation(): void {
    merge(
      this.navigationService.onNavigationStart(),
      this.destroy$
    ).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.hideNotifications()),
      filter(() => this.notifications.length > 0)
    ).subscribe();
  }

  private hideNotifications(): Observable<void> {
    const hideObservables: Observable<void>[] = this.notifications.map(notification => {
      return new Observable<void>(observer => {
        notification.hide();
        observer.next();
        observer.complete();
      });
    });

    return merge(...hideObservables);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // Skip error handling for requests with X-Skip-Error-Handler header
    if (request.headers.has('X-Skip-Error-Handler')) {
      const modifiedRequest = request.clone({
        headers: request.headers.delete('X-Skip-Error-Handler')
      });
      return next.handle(modifiedRequest);
    }

    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          let errorMessage = '';

          if (error.error instanceof ErrorEvent) {
            // client-side error
            errorMessage = `Error: ${error.error.message}`;
          } else {
            // server-side error
            errorMessage = `Error Status: ${error.status}\nMessage: ${error.message}`;
          }
          this.handleHttpError(error.status, error.message);
          //console.log(errorMessage);

          return throwError(errorMessage);
        })
      )
  }

  private handleHttpError(status: HttpStatusCode, msgStr: string): void {
    const errorMessageToShow: string = msgStr || this.getCustomErrorMessage(status) || errorMessages.CUSTOM;
    this.show(errorMessageToShow);
  }

  private getCustomErrorMessage(status: HttpStatusCode): string | null {
    switch (status) {
      case HttpStatusCode.BadRequest:
        return errorMessages.BAD_REQUEST;
      case HttpStatusCode.Unauthorized:
        return errorMessages.UNAUTHORIZED;
      case HttpStatusCode.NotFound:
        return errorMessages.NOT_FOUND;
      case HttpStatusCode.InternalServerError:
        return errorMessages.INTERNAL_SERVER;
      // Add other custom status codes and their corresponding error messages here
      default:
        return null; // Return null for unknown status codes
    }
  }

  private show(contentStr: string): void {
    if (!(contentStr.includes('401') || contentStr.includes('Unknown Error'))) {
      const notification = this.notificationService.show({
        content: contentStr
        , cssClass: "button-notification"
        , animation: { type: "slide", duration: 400 }
        , position: { horizontal: "right", vertical: "bottom" }
        , type: { style: "error", icon: true }
        , closable: true
      });
      this.notifications.push(notification);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
